import {ReactNode, useContext, useMemo, useState, useCallback, useEffect} from 'react';

import {useTranslation} from 'react-i18next';

import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import {Box, Grid, SxProps, Typography} from '@mui/material';

import {ColumnDefinition, CulturalDashboardTable} from '../shared/CulturalDashboardTable';

import {LaptopIcon, SmileIcon, HeartbeatIcon, ChartLineIcon, StarIcon} from '../../../../../../assets/company_benefits';
import EmpionDialog from '../../../../../../components/EmpionDialog/EmpionDialog';
import {LanguageContext} from '../../../../../../contexts/LanguageContext/LanguageContext';
import {greenColors, orangeColors} from '../../../../../../layouts/theme/designSystem';
import {BenefitClass, DesiredBenefits} from '../../types/CultureDashboardBenefitsTypes';

interface DesiredBenefitsCardsProps {
  desiredBenefits: DesiredBenefits;
}

export default function DesiredBenefitsCards({desiredBenefits}: DesiredBenefitsCardsProps) {
  const currentLanguage = useContext(LanguageContext);
  const {t} = useTranslation();

  const [open, setOpen] = useState<boolean>(false);
  const [benefits, setBenefits] = useState<BenefitClass[]>();

  const paperStyles: SxProps = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '16px',
    backgroundColor: 'white',
    border: `1px solid ${orangeColors[3]}`,
    boxShadow: 'none',
    borderRadius: '16px',
    width: '100%',
    flexBasis: {xs: '100%', md: '30%'},
    height: '92px',
  };

  const iconFillStyles = greenColors[700];

  const benefitIcons: {[key: string]: ReactNode} = useMemo(
    () => ({
      office: <LaptopIcon fill={iconFillStyles} aria-label='laptop icon' />,
      happiness: <SmileIcon fill={iconFillStyles} aria-label='smiling face icon' />,
      health: <HeartbeatIcon fill={iconFillStyles} aria-label='heartbeat icon' />,
      financial: <ChartLineIcon fill={iconFillStyles} aria-label='chart line icon' />,
    }),
    [iconFillStyles]
  );

  const renderIcon = useCallback(
    (category: string) => {
      return benefitIcons[category] || <StarIcon />;
    },
    [benefitIcons]
  );

  const handleClose = () => {
    setOpen(false);
  };

  const handleOpen = () => {
    setOpen(true);
  };

  useEffect(() => {
    const orderedItems = desiredBenefits.all.sort((a, b) => (b.percentage || 0) - (a.percentage || 0));
    setBenefits(orderedItems);
  }, [desiredBenefits]);

  const linkStyles: SxProps = {
    marginTop: 1,
    color: greenColors[700],
    verticalAlign: 'middle',
    cursor: 'pointer',
  };

  const desiredBenefitsColumns: ColumnDefinition<BenefitClass>[] = useMemo(() => {
    return [
      {
        label: t('companyCultureDashboard.desiredBenefitsDialog.benefitsWishes'),
        render: (item) => (
          <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
            {renderIcon(item.category)}
            {item.benefit.labels[currentLanguage]}
          </Box>
        ),
        key: 'benefit',
      },
      {
        key: 'distribution',
        label: '#',
        render: (item) => item.distribution,
      },
      {
        key: 'percentage',
        label: '%',
        render: (item) => `${Math.round(item.percentage ? item.percentage * 100 : 0)}%`,
      },
    ];
  }, [currentLanguage, t, renderIcon]);

  return (
    <Grid container>
      <Grid container item sx={{display: 'flex', alignItems: 'center', mb: 2, mt: 8}}>
        <Grid item sx={{mr: {xs: '0px', sm: '16px'}}}>
          <Typography variant='h2'>{t('companyCultureDashboard.desiredBenefitsTitle')}</Typography>
        </Grid>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
          }}
          onClick={handleOpen}
        >
          <Typography variant='body1' className='hover-target' sx={{...linkStyles}}>
            {t('companyCultureDashboard.desiredBenefitsDialog.seeAllWishes')}
          </Typography>
          <ArrowForwardIosIcon
            className='hover-target'
            sx={{fontSize: '1rem', position: 'relative', ml: 1, ...linkStyles}}
          />
        </Box>
      </Grid>
      <Grid
        item
        container
        gap={4}
        display={'grid'}
        gridTemplateColumns={{xs: '1fr', md: 'repeat(3, minmax(min-content, 1fr))'}}
      >
        {desiredBenefits.topThree &&
          desiredBenefits.topThree[currentLanguage].map((benefit, index) => {
            return (
              <Box key={index} sx={paperStyles} data-testid='recommendation-card'>
                <Typography variant='body1'>{benefit}</Typography>
              </Box>
            );
          })}
      </Grid>
      <EmpionDialog
        open={open}
        onClose={handleClose}
        sx={{'& .MuiDialogContent-root': {padding: '32px', maxHeight: '80vh'}}}
      >
        <CulturalDashboardTable
          onClose={handleClose}
          data={benefits || []}
          columns={desiredBenefitsColumns}
          isCulturalDashboard
          alternativeTitle={t('companyCultureDashboard.desiredBenefitsDialog.allWishes') as string}
        />
      </EmpionDialog>
    </Grid>
  );
}
